import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { TAX_RATE } from '../lib/estimateFinancials'
import { formatDate } from '../lib/dates'
import { formatCurrency, getInvoiceTotal } from '../lib/jobFinancials'
import QuickAddClientModal from '../components/QuickAddClientModal'

const STATUS_COLOR = { draft: '#6b7280', sent: '#2563eb', paid: '#16a34a', partial: '#d97706', overdue: '#dc2626', void: '#6b7280', cancelled: '#6b7280' }
const STATUS_BG    = { draft: '#f3f4f6', sent: '#dbeafe', paid: '#dcfce7', partial: '#fef3c7', overdue: '#fef2f2', void: '#f3f4f6', cancelled: '#f3f4f6' }

// --- Invoice List ---
export function InvoicesList() {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => { fetchInvoices() }, [])

  async function fetchInvoices() {
    const { data } = await supabase
      .from('invoices')
      .select('*, job:jobs(title), client:clients(name, email)')
      .order('created_at', { ascending: false })
    setInvoices(data || [])
    setLoading(false)
  }

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter)
  const totalUnpaid = invoices.filter(i => i.status === 'sent' || i.status === 'draft').reduce((s, i) => s + getInvoiceTotal(i), 0)
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + getInvoiceTotal(i), 0)

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Invoices</h1>
          <p style={styles.sub}>Manage and track client invoices</p>
        </div>
        <Link to="/invoices/new" style={{ ...styles.actionBtn, textDecoration: 'none', display: 'inline-block' }}>+ New Invoice</Link>
      </div>

      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderTopColor: '#dc2626' }}>
          <div style={styles.summaryLabel}>Outstanding</div>
          <div style={{ ...styles.summaryValue, color: '#dc2626' }}>{formatCurrency(totalUnpaid)}</div>
          <div style={styles.summaryNote}>{invoices.filter(i => i.status === 'sent' || i.status === 'draft').length} invoices</div>
        </div>
        <div style={{ ...styles.summaryCard, borderTopColor: '#16a34a' }}>
          <div style={styles.summaryLabel}>Collected</div>
          <div style={{ ...styles.summaryValue, color: '#16a34a' }}>{formatCurrency(totalPaid)}</div>
          <div style={styles.summaryNote}>{invoices.filter(i => i.status === 'paid').length} invoices</div>
        </div>
        <div style={{ ...styles.summaryCard, borderTopColor: '#2563eb' }}>
          <div style={styles.summaryLabel}>Total Invoiced</div>
          <div style={{ ...styles.summaryValue, color: '#2563eb' }}>{formatCurrency(invoices.reduce((s, i) => s + getInvoiceTotal(i), 0))}</div>
          <div style={styles.summaryNote}>{invoices.length} total</div>
        </div>
      </div>

      <div style={styles.filterBar}>
        {['all', 'draft', 'sent', 'paid', 'overdue'].map(s => (
          <button key={s} style={{ ...styles.filterBtn, ...(filter === s ? styles.filterBtnActive : {}) }} onClick={() => setFilter(s)}>
            {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? <div style={styles.loading}>Loading invoices...</div> : (
        <div style={styles.list}>
          {filtered.length === 0 && <div style={styles.empty}>No invoices yet. Most are created from a job's payment schedule — or start a standalone one with "+ New Invoice" above.</div>}
          {filtered.map(inv => (
            <Link key={inv.id} to={`/invoices/${inv.id}`} style={styles.invoiceRow}>
              <div style={styles.invoiceLeft}>
                <span style={styles.invoiceNum}>{inv.invoice_number || `INV-${inv.id.slice(0, 6).toUpperCase()}`}</span>
                <span style={styles.invoiceJob}>{inv.job?.title || <span style={styles.standaloneTag}>Standalone</span>}</span>
                <span style={styles.invoiceClient}>{inv.client?.name || '—'}</span>
              </div>
              <div style={styles.invoiceRight}>
                <span style={styles.invoiceAmount}>{formatCurrency(getInvoiceTotal(inv))}</span>
                <span style={{ ...styles.statusBadge, background: STATUS_BG[inv.status], color: STATUS_COLOR[inv.status] }}>
                  {inv.status}
                </span>
                {inv.due_date && <span style={styles.invoiceDue}>Due {formatDate(inv.due_date)}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Invoice Detail ---
export function InvoiceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [invoice, setInvoice] = useState(null)
  const [job, setJob] = useState(null)
  const [costs, setCosts] = useState([])
  const [timeEntries, setTimeEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editNotes, setEditNotes] = useState('')
  const [editDueDate, setEditDueDate] = useState('')
  const [statusMsg, setStatusMsg] = useState('')

  useEffect(() => { fetchAll() }, [id])

  async function fetchAll() {
    const { data: inv } = await supabase
      .from('invoices')
      .select('*, job:jobs(*, client:clients(*)), client:clients(*)')
      .eq('id', id)
      .single()
    if (!inv) { setLoading(false); return }
    setInvoice(inv)
    setEditNotes(inv.notes || '')
    setEditDueDate(inv.due_date || '')

    const [costsRes, timeRes] = await Promise.all([
      supabase.from('job_costs').select('*').eq('job_id', inv.job_id),
      supabase.from('time_entries').select('*, employee:profiles(full_name, hourly_rate)').eq('job_id', inv.job_id).not('clock_out', 'is', null)
    ])
    setJob(inv.job)
    setCosts(costsRes.data || [])
    setTimeEntries(timeRes.data || [])
    setLoading(false)
  }

  async function updateStatus(status) {
    setSaving(true)
    const updates = { status }
    if (status === 'paid') updates.paid_at = new Date().toISOString()
    if (status === 'sent') updates.sent_at = new Date().toISOString()
    await supabase.from('invoices').update(updates).eq('id', id)
    setInvoice(i => ({ ...i, ...updates }))
    setStatusMsg(`Invoice marked as ${status}`)
    setSaving(false)
    setTimeout(() => setStatusMsg(''), 3000)
  }

  async function saveNotes() {
    await supabase.from('invoices').update({ notes: editNotes, due_date: editDueDate || null }).eq('id', id)
    setInvoice(i => ({ ...i, notes: editNotes, due_date: editDueDate || null }))
    setStatusMsg('Saved')
    setTimeout(() => setStatusMsg(''), 2000)
  }

  async function deleteInvoice() {
    if (!window.confirm('Are you sure you want to permanently delete this invoice? This cannot be undone.')) return
    const { error } = await supabase.from('invoices').delete().eq('id', id)
    if (error) { alert('Failed to delete invoice: ' + error.message); return }
    navigate('/invoices')
  }

  if (loading) return <div style={styles.loading}>Loading invoice...</div>
  if (!invoice) return <div style={styles.loading}>Invoice not found. <Link to="/invoices">Back to invoices</Link></div>

  const laborCost = timeEntries.reduce((s, t) => {
    const hours = (new Date(t.clock_out) - new Date(t.clock_in)) / 3600000
    return s + (hours * (t.employee?.hourly_rate || 0))
  }, 0)
  const client = invoice.client || job?.client
  const invoiceNum = invoice.invoice_number || `INV-${id.slice(0, 6).toUpperCase()}`

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <Link to="/invoices" style={styles.backLink}>← Invoices</Link>
          <h1 style={styles.title}>{invoiceNum}</h1>
          <span style={{ ...styles.statusBadge, background: STATUS_BG[invoice.status], color: STATUS_COLOR[invoice.status], fontSize: '14px', padding: '4px 12px' }}>
            {invoice.status}
          </span>
        </div>
        <div style={styles.headerActions}>
          {statusMsg && <span style={styles.statusMsg}>{statusMsg}</span>}
          {invoice.status === 'draft' && (
            <button style={styles.actionBtn} onClick={() => updateStatus('sent')} disabled={saving}>
              📤 Mark as Sent
            </button>
          )}
          {(invoice.status === 'sent' || invoice.status === 'draft') && (
            <button style={{ ...styles.actionBtn, background: '#16a34a' }} onClick={() => updateStatus('paid')} disabled={saving}>
              ✅ Mark as Paid
            </button>
          )}
          {invoice.status === 'sent' && (
            <button style={{ ...styles.actionBtn, background: '#dc2626' }} onClick={() => updateStatus('overdue')} disabled={saving}>
              ⚠️ Mark Overdue
            </button>
          )}
          {invoice.status === 'paid' && (
            <button style={{ ...styles.actionBtn, background: '#6b7280' }} onClick={() => updateStatus('sent')} disabled={saving}>
              ↩️ Unmark as Paid
            </button>
          )}
          <button style={styles.deleteBtn} onClick={deleteInvoice}>
            🗑️ Delete
          </button>
        </div>
      </div>

      {/* Invoice Document */}
      <div style={styles.invoiceDoc}>
        {/* Invoice Header */}
        <div style={styles.docHeader}>
          <div>
            <h2 style={styles.companyName}>TLC Unlimited</h2>
            <p style={styles.companyInfo}>El Paso, TX</p>
            <p style={styles.companyInfo}>john@tlcunlimitedtx.com</p>
          </div>
          <div style={styles.docHeaderRight}>
            <div style={styles.invoiceTitle}>INVOICE</div>
            <div style={styles.invoiceNumLarge}>{invoiceNum}</div>
            <div style={styles.invoiceDate}>Date: {formatDate(invoice.created_at)}</div>
            {invoice.due_date && <div style={styles.invoiceDue2}>Due: {formatDate(invoice.due_date)}</div>}
          </div>
        </div>

        {/* Bill To */}
        {client && (
          <div style={styles.billTo}>
            <div style={styles.billToLabel}>BILL TO</div>
            <div style={styles.billToName}>{client.name}</div>
            {client.company && <div style={styles.billToInfo}>{client.company}</div>}
            {client.email && <div style={styles.billToInfo}>{client.email}</div>}
            {client.phone && <div style={styles.billToInfo}>{client.phone}</div>}
            {(client.billing_address || client.address) && <div style={styles.billToInfo}>{client.billing_address || client.address}</div>}
          </div>
        )}

        {/* Job Info */}
        {job && (
          <div style={styles.jobInfo}>
            <div style={styles.jobInfoRow}><span style={styles.jobInfoLabel}>Job</span><span>{job.title}</span></div>
            {job.job_type && <div style={styles.jobInfoRow}><span style={styles.jobInfoLabel}>Type</span><span>{job.job_type}</span></div>}
            {job.address && <div style={styles.jobInfoRow}><span style={styles.jobInfoLabel}>Location</span><span>{job.address}</span></div>}
            {job.scheduled_date && <div style={styles.jobInfoRow}><span style={styles.jobInfoLabel}>Service Date</span><span>{formatDate(job.scheduled_date)}</span></div>}
          </div>
        )}

        {/* Standalone label — not linked to a job/approved contract, per QUOTE_TO_CASH.md */}
        {!job && !loading && (
          <div style={styles.standaloneBanner}>Standalone Invoice — not linked to an approved job contract</div>
        )}

        {/* Line Items */}
        <div style={styles.lineItems}>
          <div style={styles.lineHeader}>
            <span style={{ flex: 2 }}>Description</span>
            <span style={styles.lineRight}>Amount</span>
          </div>

          {/* Contract amount as main line item */}
          {job?.contract_amount && (
            <div style={styles.lineRow}>
              <span style={{ flex: 2 }}>
                <strong>{job.title}</strong>
                {job.description && <div style={styles.lineDesc}>{job.description}</div>}
              </span>
              <span style={styles.lineRight}>{formatCurrency(job.contract_amount)}</span>
            </div>
          )}

          {/* Standalone invoice — no job to derive a description from, use the invoice's own notes */}
          {!job && (
            <div style={styles.lineRow}>
              <span style={{ flex: 2 }}><strong>{invoice.notes || 'Invoice'}</strong></span>
              <span style={styles.lineRight}>{formatCurrency(getInvoiceTotal(invoice))}</span>
            </div>
          )}

          {/* Materials breakdown if no contract amount */}
          {!job?.contract_amount && costs.map(c => (
            <div key={c.id} style={styles.lineRow}>
              <span style={{ flex: 2 }}>{c.description} <span style={styles.catTag}>{c.category}</span></span>
              <span style={styles.lineRight}>{formatCurrency(c.amount)}</span>
            </div>
          ))}

          {/* Labor */}
          {!job?.contract_amount && laborCost > 0 && (
            <div style={styles.lineRow}>
              <span style={{ flex: 2 }}>Labor ({timeEntries.reduce((s, t) => s + (new Date(t.clock_out) - new Date(t.clock_in)) / 3600000, 0).toFixed(1)} hrs)</span>
              <span style={styles.lineRight}>{formatCurrency(laborCost)}</span>
            </div>
          )}

          {/* Deposit deduction */}
          {job?.deposit_received && job?.deposit_amount > 0 && (
            <div style={{ ...styles.lineRow, color: '#16a34a' }}>
              <span style={{ flex: 2 }}>Deposit Received</span>
              <span style={styles.lineRight}>-{formatCurrency(job.deposit_amount)}</span>
            </div>
          )}

          <div style={styles.lineTotal}>
            <span style={{ flex: 2 }}>TOTAL DUE</span>
            <span style={{ ...styles.lineRight, fontSize: '20px', fontWeight: '700', color: '#1a2e1a' }}>
              {formatCurrency(getInvoiceTotal(invoice))}
            </span>
          </div>
        </div>

        {/* Notes */}
        <div style={styles.notesSection}>
          <div style={styles.notesLabel}>Notes / Payment Instructions</div>
          <textarea
            style={styles.notesInput}
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            placeholder="e.g. Please make checks payable to TLC Unlimited. Payment due within 30 days."
            rows={3}
          />
        </div>

        {/* Due Date Edit */}
        <div style={styles.dueDateRow}>
          <label style={styles.dueDateLabel}>Due Date</label>
          <input type="date" style={styles.dueDateInput} value={editDueDate} onChange={e => setEditDueDate(e.target.value)} />
          <button style={styles.saveNotesBtn} onClick={saveNotes}>Save</button>
        </div>

        {/* Stripe placeholder */}
        <div style={styles.stripePlaceholder}>
          <span style={styles.stripePlaceholderIcon}>💳</span>
          <div>
            <div style={styles.stripePlaceholderTitle}>Online Payment — Coming Soon</div>
            <div style={styles.stripePlaceholderSub}>Stripe integration will allow clients to pay by card directly from this invoice.</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// --- New Standalone Invoice (Phase 5, gap a) ---
// Every existing invoice-creation path (deposit/milestone/final/change_order, all in
// Jobs.js) requires a job. This is the one that doesn't -- for small one-off jobs,
// receipts, or work done before Field Canopy existed, per QUOTE_TO_CASH.md's
// Standalone Invoice Requirements. No line-items sub-table exists anywhere in this
// app for any invoice type (every existing type is a single amount + notes) -- this
// matches that same pattern rather than inventing an itemized model just for this one
// type, which would be an odd asymmetry.
export function NewStandaloneInvoice() {
  const navigate = useNavigate()
  const [clients, setClients] = useState([])
  const [jobs, setJobs] = useState([])
  const [showAddClient, setShowAddClient] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    client_id: '', job_id: '', service_date: new Date().toISOString().split('T')[0],
    amount: '', taxable: false, notes: '',
  })

  useEffect(() => {
    supabase.from('clients').select('id, name').eq('active', true).order('name').then(({ data }) => setClients(data || []))
    supabase.from('jobs').select('id, title').order('created_at', { ascending: false }).then(({ data }) => setJobs(data || []))
  }, [])

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.client_id) { setError('Select a client.'); return }
    const amount = parseFloat(form.amount)
    if (!amount || amount <= 0) { setError('Enter a valid amount.'); return }
    setSaving(true)
    setError('')

    const taxAmount = form.taxable ? amount * TAX_RATE : 0
    const dueDate = new Date(form.service_date || Date.now())
    dueDate.setDate(dueDate.getDate() + 30)

    const { data, error: insertErr } = await supabase.from('invoices').insert([{
      client_id: form.client_id,
      job_id: form.job_id || null,
      invoice_number: `INV-${Date.now().toString().slice(-6)}`,
      amount, status: 'draft', invoice_type: 'other',
      due_date: dueDate.toISOString().split('T')[0],
      tax_rate: form.taxable ? TAX_RATE : 0,
      tax_amount: taxAmount,
      notes: form.notes || null,
    }]).select().single()

    setSaving(false)
    if (insertErr) {
      // job_id is nullable per this feature's design, but that was never confirmed
      // against the live schema (invoices predates tracked migrations) -- if it turns
      // out not to be, this is the one place that would fail, with a clear message
      // instead of a silent break. See Docs/DECISIONS_NEEDED.md if this fires.
      if (!form.job_id && /job_id/i.test(insertErr.message) && /null/i.test(insertErr.message)) {
        setError('This database currently requires every invoice to have a job — standalone invoices need a quick one-line fix first. See Docs/DECISIONS_NEEDED.md.')
      } else {
        setError('Failed to create invoice: ' + insertErr.message)
      }
      return
    }
    navigate(`/invoices/${data.id}`)
  }

  return (
    <div style={styles.page}>
      <Link to="/invoices" style={styles.backLink}>← Back to Invoices</Link>
      <h1 style={styles.title}>New Invoice</h1>
      <p style={styles.sub}>Standalone — not linked to an approved job contract unless you link a job below</p>

      <form onSubmit={handleSubmit} style={standaloneStyles.form}>
        {error && <div style={standaloneStyles.error}>{error}</div>}

        <div style={standaloneStyles.formGrid}>
          <div>
            <label style={standaloneStyles.label}>Client *</label>
            <select
              style={standaloneStyles.input}
              value={form.client_id}
              onChange={e => {
                if (e.target.value === '__add_new__') { setShowAddClient(true); return }
                set('client_id', e.target.value)
              }}
            >
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="__add_new__">+ Add New Client</option>
            </select>
          </div>

          <div>
            <label style={standaloneStyles.label}>Link to Job (optional)</label>
            <select style={standaloneStyles.input} value={form.job_id} onChange={e => set('job_id', e.target.value)}>
              <option value="">No job — standalone</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </div>

          <div>
            <label style={standaloneStyles.label}>Service Date</label>
            <input style={standaloneStyles.input} type="date" value={form.service_date} onChange={e => set('service_date', e.target.value)} />
          </div>

          <div>
            <label style={standaloneStyles.label}>Amount *</label>
            <input style={standaloneStyles.input} type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={e => set('amount', e.target.value)} />
          </div>
        </div>

        <label style={standaloneStyles.checkboxRow}>
          <input type="checkbox" checked={form.taxable} onChange={e => set('taxable', e.target.checked)} />
          Taxable ({(TAX_RATE * 100).toFixed(2)}%)
        </label>

        <div>
          <label style={standaloneStyles.label}>Notes / Description</label>
          <textarea
            style={{ ...standaloneStyles.input, minHeight: '80px', resize: 'vertical' }}
            placeholder="What this invoice is for — shown as the line item description"
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
          />
        </div>

        {form.taxable && form.amount && (
          <div style={standaloneStyles.totalPreview}>
            Total: ${(parseFloat(form.amount || 0) + parseFloat(form.amount || 0) * TAX_RATE).toFixed(2)}
            <span style={{ color: '#6b7c6b', fontWeight: '400' }}> (${parseFloat(form.amount || 0).toFixed(2)} + ${(parseFloat(form.amount || 0) * TAX_RATE).toFixed(2)} tax)</span>
          </div>
        )}

        <button style={styles.actionBtn} type="submit" disabled={saving}>
          {saving ? 'Creating...' : 'Create Invoice'}
        </button>
      </form>

      {showAddClient && (
        <QuickAddClientModal
          onClose={() => setShowAddClient(false)}
          onCreated={(newClient) => {
            setClients(cs => [...cs, newClient].sort((a, b) => a.name.localeCompare(b.name)))
            set('client_id', newClient.id)
            setShowAddClient(false)
          }}
        />
      )}
    </div>
  )
}

const standaloneStyles = {
  form: { background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '640px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
  label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', fontWeight: '600' },
  error: { background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' },
  totalPreview: { fontSize: '15px', fontWeight: '700', color: '#1a2e1a', background: '#f9fafb', padding: '10px 14px', borderRadius: '8px' },
}

const styles = {
  page: { width: '100%' },
  loading: { padding: '40px', textAlign: 'center', color: '#6b7c6b' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  title: { margin: '0 0 6px', fontSize: '26px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: 0, color: '#6b7c6b', fontSize: '13px' },
  backLink: { display: 'inline-block', color: '#6b7c6b', textDecoration: 'none', fontSize: '13px', marginBottom: '8px' },
  headerActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  actionBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '9px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  deleteBtn: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '9px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  statusMsg: { fontSize: '13px', color: '#16a34a', fontWeight: '600' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '16px' },
  summaryCard: { background: 'white', borderRadius: '10px', padding: '16px', borderTop: '3px solid', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  summaryLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' },
  summaryValue: { fontSize: '22px', fontWeight: '700', margin: '4px 0 2px' },
  summaryNote: { fontSize: '12px', color: '#9ca3af' },
  filterBar: { display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' },
  filterBtn: { padding: '6px 14px', borderRadius: '20px', border: '1.5px solid #e5e7eb', background: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#6b7c6b' },
  filterBtnActive: { background: '#2d4a2d', color: 'white', borderColor: '#2d4a2d' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  empty: { padding: '30px', textAlign: 'center', color: '#9ca3af', background: 'white', borderRadius: '10px' },
  invoiceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: 'white', borderRadius: '10px', textDecoration: 'none', border: '1px solid #e5e7eb', gap: '12px' },
  invoiceLeft: { display: 'flex', flexDirection: 'column', gap: '3px' },
  invoiceNum: { fontWeight: '700', color: '#1a2e1a', fontSize: '14px' },
  invoiceJob: { fontSize: '13px', color: '#374151' },
  invoiceClient: { fontSize: '12px', color: '#9ca3af' },
  invoiceRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' },
  invoiceAmount: { fontWeight: '700', fontSize: '16px', color: '#1a2e1a' },
  invoiceDue: { fontSize: '12px', color: '#9ca3af' },
  statusBadge: { padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', textTransform: 'capitalize', display: 'inline-block' },
  standaloneTag: { fontSize: '11px', fontWeight: '700', color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.4px' },
  standaloneBanner: { background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px', fontSize: '12.5px', fontWeight: '600', color: '#6d28d9' },
  // Invoice document
  invoiceDoc: { background: 'white', borderRadius: '14px', padding: '36px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' },
  docHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', paddingBottom: '24px', borderBottom: '2px solid #f3f4f6' },
  companyName: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#1a2e1a' },
  companyInfo: { margin: '2px 0', fontSize: '13px', color: '#6b7c6b' },
  docHeaderRight: { textAlign: 'right' },
  invoiceTitle: { fontSize: '28px', fontWeight: '700', color: '#1a2e1a', letterSpacing: '2px' },
  invoiceNumLarge: { fontSize: '16px', fontWeight: '600', color: '#6b7c6b', margin: '4px 0' },
  invoiceDate: { fontSize: '13px', color: '#6b7c6b' },
  invoiceDue2: { fontSize: '13px', color: '#dc2626', fontWeight: '600' },
  billTo: { marginBottom: '24px', padding: '16px', background: '#f9fafb', borderRadius: '8px' },
  billToLabel: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' },
  billToName: { fontWeight: '700', fontSize: '16px', color: '#1a2e1a', marginBottom: '4px' },
  billToInfo: { fontSize: '13px', color: '#6b7c6b', margin: '2px 0' },
  jobInfo: { marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '6px' },
  jobInfoRow: { display: 'flex', gap: '12px', fontSize: '13px' },
  jobInfoLabel: { fontWeight: '700', color: '#9ca3af', minWidth: '100px', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px', paddingTop: '1px' },
  lineItems: { borderRadius: '8px', overflow: 'hidden', marginBottom: '24px', border: '1px solid #e5e7eb' },
  lineHeader: { display: 'flex', padding: '12px 16px', background: '#1a2e1a', color: 'white', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' },
  lineRow: { display: 'flex', padding: '14px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '14px', color: '#374151' },
  lineDesc: { fontSize: '12px', color: '#9ca3af', marginTop: '3px' },
  lineRight: { textAlign: 'right', fontWeight: '600', minWidth: '100px' },
  catTag: { background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', marginLeft: '6px' },
  lineTotal: { display: 'flex', padding: '16px', background: '#f9fafb', borderTop: '2px solid #e5e7eb', fontSize: '14px', fontWeight: '700', color: '#1a2e1a' },
  notesSection: { marginBottom: '16px' },
  notesLabel: { fontSize: '12px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  notesInput: { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', color: '#374151' },
  dueDateRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  dueDateLabel: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  dueDateInput: { padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit' },
  saveNotesBtn: { background: '#2d4a2d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  stripePlaceholder: { display: 'flex', gap: '14px', alignItems: 'center', background: '#f0f9ff', border: '1.5px dashed #93c5fd', borderRadius: '10px', padding: '16px', marginTop: '8px' },
  stripePlaceholderIcon: { fontSize: '28px' },
  stripePlaceholderTitle: { fontWeight: '700', color: '#1e3a5f', fontSize: '14px', marginBottom: '3px' },
  stripePlaceholderSub: { fontSize: '12px', color: '#6b7c6b' }
}
